import React, { useState, useEffect } from "react";
import { Alert } from "react-bootstrap";
import { ActionHandlerFunction } from "@preveil-api";
import {
  Account, AdminErrorMessages, AdminUIActionTypes, LOAD_CHARTS_RETRY_MAX, MessageHandlerDisplayType, OrganizationSettings, useAppDispatch, usePostUsersOrgsByEntityIdKeyvalAndKeyQuery, UsageSecuritySettings, OrgKeyValPair,
  parseStringToBoolean, isUsageSecurityKey
} from "src/common";
import { collectionApi } from "src/common/api/collection/collection-rtk-api";
import { InfoPopover, Loading } from "src/components";
import ErrorPanel from "./ErrorPanel.component";
import { adminActions } from "src/store";

type AllProps = {
  current_account: Account;
  org_id: string;
  handleAction: ActionHandlerFunction;
}

// Description: Render the security Status for the dashboard
function DashboardSettingsComponent(props: AllProps) {
  const { current_account, org_id, handleAction } = props;
  const dispatch = useAppDispatch();
  const [security_settings, setSecuritySettings] = useState<UsageSecuritySettings | undefined>(undefined);
  const [retry, setRetry] = useState<number>(1);
  const { data, error, isLoading } = usePostUsersOrgsByEntityIdKeyvalAndKeyQuery({
    account_ids: Account.getAccountIdentifiers(current_account),
    body: {
      entityId: org_id,
      keys: [OrganizationSettings.present_cui_notice,
      OrganizationSettings.disable_file_export,
      OrganizationSettings.require_biometrics,
      OrganizationSettings.allow_account_creation
      ]
    }
  });

  useEffect(() => {
    !!data && handleSecuritySettings(data.keyval_pairs);
  }, [data]);

  // Description: Set the Security Settings to state
  function handleSecuritySettings(keyval_pairs: OrgKeyValPair[]) {
    const new_settings = keyval_pairs.reduce<UsageSecuritySettings>((obj, item) => {
      if (isUsageSecurityKey(item.key)) {
        obj[item.key] = parseStringToBoolean(item.value);
      }
      return obj;
    }, {
      present_cui_notice: true,
      disable_file_export: true,
      require_biometrics: true,
    });
    setSecuritySettings(new_settings);
    // Dispatch to Redux store for PDF generator
    dispatch(adminActions.setSecuritySettings(new_settings));
  }

  return (
    isLoading ? <Loading className="in-place" /> :
      !!error ? <ErrorPanel
        enable_retry={(retry >= 0)}
        handleAction={() => {
          if (retry < LOAD_CHARTS_RETRY_MAX) {
            dispatch(collectionApi.util.invalidateTags(["keyval"]));
            setRetry(Number(retry + 1));
          } else {
            handleAction({
              actionType: AdminUIActionTypes.PageErrorMessage,
              params: {
                message: AdminErrorMessages.error_security_settings_retries_expired,
                display_type: MessageHandlerDisplayType.toastr
              }
            });
            setRetry(-1); // NOTE: This will remove the button on the Error Panel
          }
        }} /> :
        <>
          <h3>Security Status</h3>
          {security_settings && (
            <>
              <div>
                <h5>Controlled Unclassified Information (CUI)</h5>
                <Alert variant="info" className="admin-alert admin-switch">
                  <div className="switch-preveil-container">
                    <p className="switch-text-legend">
                      <span>The CUI banner is displayed on PreVeil applications.</span>
                      <InfoPopover message="For environments that process controlled unclassified information, this banner is required for regulatory compliance." />
                    </p>
                    <div className="switch-preveil align-self-center">
                      <span>{security_settings.present_cui_notice ? "Yes" : "No"}</span>
                    </div>
                  </div>
                </Alert>
              </div>
              <div>
                <h5>Mobile Phone File Export</h5>
                <Alert variant="info" className="admin-alert admin-switch">
                  <div className="switch-preveil-container">
                    <p className="switch-text-legend">
                      {/* NOTE: The "security_settings.disable_file_export" is inverted because the API returns "false" for enabled and "true" for disabled */}
                      <span>PreVeil files can be downloaded to mobile devices.</span>
                      <InfoPopover message="This switch determines if the user is allowed to download PreVeil files to their mobile device." />
                    </p>
                    <div className="switch-preveil align-self-center">
                      <span>{!security_settings.disable_file_export ? "Yes" : "No"}</span>
                    </div>
                  </div>
                </Alert>
              </div>
              <div>
                <h5>Biometric Requirements</h5>
                <Alert variant="info" className="admin-alert admin-switch">
                  <div className="switch-preveil-container">
                    <p className="switch-text-legend">
                      <span>Biometrics are required for login on the PreVeil mobile app.</span>
                      <InfoPopover message="When biometrics are required, the device's native biometric sensor will be a required step in the login process whenever the app is launched." />
                    </p>
                    <div className="switch-preveil align-self-center">
                      <span>{security_settings.require_biometrics ? "Yes" : "No"}</span>
                    </div>
                  </div>
                </Alert>
              </div>
            </>
          )}
        </>
  );
}

export default React.memo(DashboardSettingsComponent);